import { useEffect, useMemo, useState } from 'react';
import { UserRole } from '@sportspredict/shared';

export function useCrowdLockKickoff({
	isCrowdLockMode,
	lobbyResponse,
	user,
}: {
	isCrowdLockMode: boolean;
	lobbyResponse: any;
	user: any;
}) {
	const [hasKickoffStarted, setHasKickoffStarted] = useState(() => {
		if (!isCrowdLockMode || !lobbyResponse?.game_start_date) return false;
		const t = new Date(lobbyResponse.game_start_date).getTime();
		return Number.isFinite(t) && Date.now() >= t;
	});

	useEffect(() => {
		if (!isCrowdLockMode) return;

		const t = lobbyResponse?.game_start_date
			? new Date(lobbyResponse.game_start_date).getTime()
			: NaN;
		if (!Number.isFinite(t)) return;

		if (Date.now() >= t) {
			setHasKickoffStarted(true);
			return;
		}

		setHasKickoffStarted(false);

		const ms = t - Date.now();
		const id = window.setTimeout(() => {
			setHasKickoffStarted(true);
		}, ms);

		return () => window.clearTimeout(id);
	}, [isCrowdLockMode, lobbyResponse?.game_start_date]);

	const isCrowdLockStarted = useMemo(() => {
		if (!isCrowdLockMode) return false;
		if (user?.role === UserRole.Admin) return false;
		return hasKickoffStarted;
	}, [isCrowdLockMode, user?.role, hasKickoffStarted]);

	const CROWDLOCK_INTRO_MODAL_KEY = useMemo(
		() => (user?.id ? `crowdlockIntroShown:${user.id}` : null),
		[user?.id]
	);
	const [showCrowdLockModal, setShowCrowdLockModal] = useState(false);

	useEffect(() => {
		if (!isCrowdLockMode) {
			setShowCrowdLockModal(false);
			return;
		}

		if (!CROWDLOCK_INTRO_MODAL_KEY) {
			setShowCrowdLockModal(true);
			return;
		}

		const alreadyShown = localStorage.getItem(CROWDLOCK_INTRO_MODAL_KEY) === '1';
		setShowCrowdLockModal(!alreadyShown);

		if (!alreadyShown) {
			localStorage.setItem(CROWDLOCK_INTRO_MODAL_KEY, '1');
		}
	}, [isCrowdLockMode, CROWDLOCK_INTRO_MODAL_KEY]);

	return {
		hasKickoffStarted,
		setHasKickoffStarted,
		isCrowdLockStarted,
		showCrowdLockModal,
		setShowCrowdLockModal,
	};
}
